import React, { useState, useEffect } from 'react';
import GameScreen from './components/GameScreen';
import MenuScreen from './components/MenuScreen';
import GameOverScreen from './components/GameOverScreen';
import VictoryScreen from './components/VictoryScreen';
import LevelTransition from './components/LevelTransition';
import { GAME_CONFIG, getNextLevelId, shouldShowTransitionBefore } from './config/gameConfig';
import { draftTextManager } from './utils/draftTextManager';
import { audioManager } from './utils/audioManager';
import './App.css';

function App() {
  const [gameState, setGameState] = useState('menu');
  const [currentLevel, setCurrentLevel] = useState(GAME_CONFIG.levelOrder[0]);
  const [collectedDrafts, setCollectedDrafts] = useState(0);
  const [totalScore, setTotalScore] = useState(0);
  const [currentFearMessage, setCurrentFearMessage] = useState(null);
  const [audioInitialized, setAudioInitialized] = useState(false);

  // Initialize audio on first user interaction
  useEffect(() => {
    const handleInteraction = () => {
      if (!audioInitialized) {
        console.log('User interaction detected - initializing audio');
        audioManager.init();
        audioManager.resumeContext();
        setAudioInitialized(true);
        // Remove listeners after first interaction
        window.removeEventListener('click', handleInteraction);
        window.removeEventListener('keydown', handleInteraction);
        window.removeEventListener('touchstart', handleInteraction);
      }
    };

    window.addEventListener('click', handleInteraction);
    window.addEventListener('keydown', handleInteraction);
    window.addEventListener('touchstart', handleInteraction);

    return () => {
      window.removeEventListener('click', handleInteraction);
      window.removeEventListener('keydown', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
    };
  }, [audioInitialized]);

  const startGame = () => {
    console.log('Starting game');
    // Ensure audio is initialized
    if (!audioInitialized) {
      audioManager.init();
      setAudioInitialized(true);
    }
    
    // Reset draft text manager for new game session
    draftTextManager.reset();
    
    setGameState('playing');
    setCurrentLevel(GAME_CONFIG.levelOrder[0]);
    setCollectedDrafts(0);
    setTotalScore(0);
  };

  const nextLevel = (score) => {
    setTotalScore(prev => prev + score);
    const nextLevelId = getNextLevelId(currentLevel);
    
    if (!nextLevelId) {
      // No more levels - victory
      setGameState('victory');
      return;
    }

    // Check if transition should appear before next level
    if (shouldShowTransitionBefore(nextLevelId)) {
      setGameState('transition');
    } else {
      // Normal progression
      setCurrentLevel(nextLevelId);
      setCollectedDrafts(0);
    }
  };

  const handleTransitionComplete = () => {
    const nextLevelId = getNextLevelId(currentLevel);
    if (nextLevelId) {
      setCurrentLevel(nextLevelId);
      setCollectedDrafts(0);
      setGameState('playing');
    }
  };

  const gameOver = (fearMessage) => {
    setCurrentFearMessage(fearMessage);
    setGameState('gameOver');
  };

  const restartLevel = () => {
    setGameState('playing');
    setCollectedDrafts(0);
  };

  const backToMenu = () => {
    setGameState('menu');
    setCurrentLevel(GAME_CONFIG.levelOrder[0]);
    setCollectedDrafts(0);
    setTotalScore(0);
    // Reset draft texts when going back to menu
    draftTextManager.reset();
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white overflow-hidden">
      {gameState === 'menu' && (
        <MenuScreen onStartGame={startGame} />
      )}
      {gameState === 'playing' && (
        <GameScreen 
          level={currentLevel} 
          onNextLevel={nextLevel} 
          onGameOver={gameOver}
          collectedDrafts={collectedDrafts}
          setCollectedDrafts={setCollectedDrafts}
        />
      )}
      {gameState === 'transition' && (
        <LevelTransition onComplete={handleTransitionComplete} />
      )}
      {gameState === 'gameOver' && (
        <GameOverScreen 
          fearMessage={currentFearMessage} 
          onRestartLevel={restartLevel} 
          onBackToMenu={backToMenu} 
        />
      )}
      {gameState === 'victory' && (
        <VictoryScreen 
          totalScore={totalScore} 
          onBackToMenu={backToMenu} 
          onRestart={startGame}
        />
      )}
    </div>
  );
}

export default App;